import type { HTMLAttributes, ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, type LucideIcon, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * InlineMessage — lekki komunikat bez tła (Figma „08.7 · Feedback / Inline message").
 *
 * Ikona 16px w kolorze base tonu + tekst „small" w kolorze `*-text`. Bez tła, bez ramki —
 * do walidacji pod polami formularza i drobnych podpowiedzi. Znaczenie niosą ikona + tekst.
 * Tony na STAŁYCH tokenach semantycznych (index.css).
 */

export type InlineMessageTone = 'info' | 'success' | 'warning' | 'error';

const TONE_ICON: Record<InlineMessageTone, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
};

const TONE_ICON_COLOR: Record<InlineMessageTone, string> = {
  info: 'text-[rgb(var(--color-info))]',
  success: 'text-[rgb(var(--color-success))]',
  warning: 'text-[rgb(var(--color-warning))]',
  error: 'text-[rgb(var(--color-error))]',
};

const TONE_TEXT_COLOR: Record<InlineMessageTone, string> = {
  info: 'text-[rgb(var(--color-info-text))]',
  success: 'text-[rgb(var(--color-success-text))]',
  warning: 'text-[rgb(var(--color-warning-text))]',
  error: 'text-[rgb(var(--color-error-text))]',
};

export interface InlineMessageProps extends HTMLAttributes<HTMLParagraphElement> {
  tone?: InlineMessageTone;
  children?: ReactNode;
  /** Nadpisanie ikony (domyślnie wg tonu). */
  icon?: LucideIcon;
}

export function InlineMessage({
  tone = 'error',
  children,
  icon,
  className,
  ...rest
}: InlineMessageProps) {
  const IconComponent = icon ?? TONE_ICON[tone];
  return (
    <p
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-1.5 text-[13px] leading-5',
        TONE_TEXT_COLOR[tone],
        className,
      )}
      {...rest}
    >
      <IconComponent
        size={16}
        aria-hidden="true"
        className={cn('mt-0.5 shrink-0', TONE_ICON_COLOR[tone])}
      />
      <span className="min-w-0">{children}</span>
    </p>
  );
}
